/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import Stack from "./ui/Stack";
import Calendar from "./calendar/Calendar";
import TimeSlots from "./TimeSlots";
import CalendarBookedState from "./calendar/CalendarBookedState";
import { useFirstAvailableDay } from "../lib/react-query/useFirstAvailableDay";
import { useTimeSlots } from "../lib/react-query/useTimeSlots";
import { useSchedule } from "../lib/react-query/useSchedule";
import { servicesRequireMembership } from "../utils/membershipGrant";
import usePluginUtils from "../hooks/usePluginUtils";
import LoadingIndicator from "./LoadingIndicator";

const TimeSlotPicker = ({
  date,
  onDateChange,
  bookingId,
  services,
  provider,
  location,
  minDate,
  maxDate,
  selectedTime,
  pendingTime,
  onSlotClick,
  disabled,
}) => {
  const isAnyProvider = provider?.id === "any";
  const groupCount = services.find((s) => s.group_booking_count)?.group_booking_count || 1;
  const serviceIds = services.map((s) => s.id);
  const productIds = services.flatMap((s) => [...(s.addons ?? []), ...(s.products ?? [])].map((p) => p.id));

  const { isPluginEnabled } = usePluginUtils();
  // When rescheduling, pass booking_id and omit service_id/provider_id: the backend
  // resolves them from the booking, and for a sequential batch computes availability
  // across the whole chain (not just this one service).
  const queryBase = {
    ...(bookingId
      ? { count: groupCount, location_id: location?.id, booking_id: bookingId }
      : {
        count: groupCount,
        provider_id: isAnyProvider ? null : provider?.id,
        "service_id[]": serviceIds,
        location_id: location?.id,
      }),
    ...(productIds.length > 0 && { "products[]": productIds, "product_ids[]": productIds }),
  };

  const requiresMembership = servicesRequireMembership(services);

  const {
    isLoading: isLoadingFirstAvailableDay,
    isSuccess: isSuccessFirstAvailableDay,
    data: firstAvailableDay,
  } = useFirstAvailableDay(
    {
      ...(requiresMembership ? { ...queryBase, check_membership: true } : queryBase),
      ...(minDate && { start_datetime: `${minDate} 00:00:00` }),
    },
    { enabled: serviceIds.length > 0 }
  );

  // "Available slots count" plugin: slots carry spots left.
  const slotCounts = isPluginEnabled("slots_count") ? { with_available_slots: 1 } : {};

  const { isFetching: isFetchingSlots, data: slots } = useTimeSlots(
    { ...queryBase, ...slotCounts, date_from: date, date_to: date },
    { enabled: !!date && serviceIds.length > 0 }
  );

  const nextAvailableDay =
    firstAvailableDay && firstAvailableDay > date && (!maxDate || firstAvailableDay <= maxDate)
      ? firstAvailableDay
      : null;
  // maxDate only comes from a membership grant, so this stays false for reschedule.
  const coverageExhausted =
    !!maxDate && isSuccessFirstAvailableDay && (!firstAvailableDay || firstAvailableDay > maxDate)
  const noSlots = !isFetchingSlots && !!slots && slots?.length === 0;
  // Rescheduling never joins a waiting list — WaitingListDetails reads the
  // booking selection from bookingSlice, which a reschedule does not populate.
  const waitingListEnabled = isPluginEnabled("waiting_list") && !bookingId;

  // An entry needs a day the provider works but is fully booked. The calendar disables
  // is_day_off days, but the initially selected date is never checked, so it is checked here.
  const { data: selectedDaySchedule, isLoading: isCheckingWaitingList } = useSchedule(
    { ...queryBase, date_from: date, date_to: date },
    { enabled: waitingListEnabled && noSlots && !!date }
  );

  const canJoinWaitingList =
    waitingListEnabled &&
    noSlots &&
    selectedDaySchedule?.[0]?.is_day_off === false &&
    selectedDaySchedule[0].is_wl_available !== false;

  const handleBookedStateButtonClick = () => {
    if (nextAvailableDay) onDateChange(nextAvailableDay);
  };

  return (
    <Stack $gap="1.5rem">
      <Calendar
        date={date}
        services={services}
        provider={provider}
        minDate={minDate}
        maxDate={maxDate}
        onDayClick={onDateChange}
        onMonthChange={onDateChange}
      />
      <TimeSlots
        isLoading={isFetchingSlots}
        slots={slots}
        selectedTime={selectedTime}
        pendingTime={pendingTime}
        onSlotClick={onSlotClick}
        disabled={disabled}
      />
      {noSlots &&
        (isCheckingWaitingList ? (
          <LoadingIndicator />
        ) : (
          <CalendarBookedState
            firstAvailableDay={nextAvailableDay}
            coverageExhausted={coverageExhausted}
            onButtonClick={handleBookedStateButtonClick}
            selectedDate={date}
            canJoinWaitingList={canJoinWaitingList}
          />
        ))}
    </Stack>
  );
};

export default TimeSlotPicker;
